import React from 'react';

import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {Block, Skeleton} from '@udemy/react-reveal-components';
import {PlanPrice} from '@udemy/subscription-browse';
import {useFormatCurrency} from '@udemy/ud-data';

import {useGwgSubscriptionPrice} from 'src/hooks/useGwgSubscriptionPrice';

import styles from './gwg-price-display.module.less';

export interface GwgPriceDisplayProps {
    /**
     * Optional custom copy template with %(price)s placeholder.
     * Defaults to: "Flexible purchase options starting at <span class="price">%(price)s</span>/month"
     */
    copyTemplate?: string;
    /**
     * Optional custom fallback text when pricing is unavailable.
     * Defaults to: "Flexible purchase options available"
     */
    fallbackText?: string;
    /**
     * Optional className for the skeleton loading state
     */
    skeletonClassName?: string;
}

/**
 * Displays GwG subscription pricing with loading state and discount support.
 *
 * Handles:
 * - Loading skeleton
 * - Dynamic pricing from useGwgSubscriptionPrice hook
 * - Strikethrough pricing when discounts are available
 * - Fallback text when pricing is unavailable
 * - Customizable copy template for different contexts
 *
 * @example
 * // Default usage
 * <GwgPriceDisplay />
 * // Shows: "Flexible purchase options starting at $XX.XX/month"
 *
 * @example
 * // Custom copy
 * <GwgPriceDisplay copyTemplate="Starting at <span class='price'>%(price)s</span> per month" />
 */
export const GwgPriceDisplay: React.FC<GwgPriceDisplayProps> = ({
    copyTemplate,
    fallbackText,
    skeletonClassName,
}) => {
    const {gettext} = useI18n();
    const {formatCurrency} = useFormatCurrency();
    const {formattedMonthlyAmount, discount, isLoading: isPriceLoading} = useGwgSubscriptionPrice();

    const defaultCopyTemplate = gettext(
        'Flexible purchase options starting at <span class="price">%(price)s</span>/month',
    );
    const defaultFallbackText = gettext('Flexible purchase options available');

    if (isPriceLoading) {
        return (
            <Skeleton>
                <Block className={skeletonClassName || styles['price-skeleton']} />
            </Skeleton>
        );
    }

    if (!formattedMonthlyAmount) {
        return <>{fallbackText || defaultFallbackText}</>;
    }

    const priceElement = (
        <PlanPrice
            listPriceText={
                discount?.subscriptionNonDiscountMonthlyPrice
                    ? formatCurrency(discount.subscriptionNonDiscountMonthlyPrice)
                    : formattedMonthlyAmount
            }
            discountPriceText={discount ? formattedMonthlyAmount : undefined}
        />
    );

    return (
        <LocalizedHtml
            html={copyTemplate || defaultCopyTemplate}
            interpolate={{
                price: priceElement,
            }}
        />
    );
};
